import {
    APIResponse,
    Filter,
    FilterFieldOption,
    FilterFieldOptionRequest,
    FilterFieldOptionResponse,
    SimpleMultiSeries,
    TableFilterConfig,
} from '@core/interfaces/system/system-common';
import {Observable} from 'rxjs';
import {Graph} from '@core/interfaces/common/pages';
import {DownloadCSVUrl} from '../workflow-validation';
import {ProbabilityTypeEnum} from '@core/interfaces/engin/analyzer';
import {CompareByType} from '../../../../pages/program-management/outcomes/outcomes.service';

/*
 * Response
 */
export interface OutcomeChartResponse {
    chartData: ClusteredSeries[];
}

export interface ClusteredSeries {
    code: string;
    name: string;
    graphTitle: string;
    graphId: string;
    legend: string[];
    labels: string[];
    data: number[][];
    unit: Unit;
}

export interface Unit {
    prefix: string[];
    suffix: string[];
    symbol: string[];
}

export interface OutcomeKpiResponse {
    kpiDataList: KpiDataResp[];
}

export interface KpiDataResp {
    title: string;
    subtitle: string;
    unit: string;
    residualValue: number;
    delta: number;
}

/*
 * Request
 */
export interface PmDataRequest {
    workflowItemId: number;
    sensitivityId: number;
}

export interface InvestmentOverviewRequest {
    workflowItemId: number;
    aggregateBy: 'SEGMENT' | 'PROGRAM' | 'PROJECT' | 'YEAR';
    filterList: Filter[];
    probabilityType: ProbabilityTypeEnum;
}

export interface OverlappingAssetRequest {
    projectIds: number[];
    programIds: number[];
    segmentIds: number[];
}

export interface OverlappingAssetResponse {
    value: number;
    unit: {
        prefix: string;
        suffix: string;
        symbol: string;
    };
}

export interface OutcomeChartRequest {
    sensitivityId: number;
    workflowItemId: number;
    graphList: Graph[];
    filters: Filter[];
    probabilityType: ProbabilityTypeEnum;
}

/*
 * Enums
 */
export enum OutcomeMaintainType {
    PAST = 'PAST',
    APPROACHING = 'APPROACHING',
}

/*
 * DTOs
 */
export interface PmHeaderFilterData {
    program: PmHeaderFilterValue[];
    segment: PmHeaderFilterValue[];
    project: PmHeaderFilterValue[];
    probabilityType: ProbabilityTypeEnum;
}

export interface PmInvestmentHeaderFilterData extends PmHeaderFilterData {
    compareBy: CompareByType;
}
export interface PmHeaderFilterValue {
    key: string;
    name: string;
    value: string | number;
}

export interface FilteredOptionsList {
    segment: FilterFieldOption[];
    program: FilterFieldOption[];
    project: FilterFieldOption[];
}

export abstract class PmReportingService {
    /*
     * APIs for Investment Overview
     */
    abstract getInvestmentOverview(
        param: InvestmentOverviewRequest,
    ): Observable<APIResponse<SimpleMultiSeries<string>>>;

    /*
     * APIs for Overlapping Assets
     */
    abstract getAssetOverlapBetweenProjects(
        request: OverlappingAssetRequest,
    ): Observable<APIResponse<OverlappingAssetResponse>>;

    abstract getAssetOverlapBetweenProjectsCsv(
        request: OverlappingAssetRequest,
    ): Observable<APIResponse<DownloadCSVUrl>>;

    /*
     * APIs for Outcomes (legacy Optimizer)
     */
    abstract getChartDataSummarySpending(request: OutcomeChartRequest): Observable<APIResponse<OutcomeChartResponse>>;

    abstract getChartDataSummaryOutcomes(request: OutcomeChartRequest): Observable<APIResponse<OutcomeKpiResponse>>;

    abstract getChartDataProactiveForecast(request: OutcomeChartRequest): Observable<APIResponse<OutcomeChartResponse>>;

    abstract getChartDataReactiveForecast(request: OutcomeChartRequest): Observable<APIResponse<OutcomeChartResponse>>;

    abstract getChartDataFailureForecast(request: OutcomeChartRequest): Observable<APIResponse<OutcomeChartResponse>>;

    abstract getChartDataOutageForecast(request: OutcomeChartRequest): Observable<APIResponse<OutcomeChartResponse>>;

    abstract getChartDataRiskForecastByImpact(
        request: OutcomeChartRequest,
    ): Observable<APIResponse<OutcomeChartResponse>>;

    abstract getChartDataRiskForecastByCategory(
        request: OutcomeChartRequest,
    ): Observable<APIResponse<OutcomeChartResponse>>;

    abstract getFilterFieldOptions(body: FilterFieldOptionRequest): Observable<APIResponse<FilterFieldOptionResponse>>;
}
